import { metrics, registry } from '@geo-admin/shared/contract/api/index.ts';
import type { QueryMetricsRow } from '@geo-admin/shared/contract/api/metrics.ts';
import type { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { DataTable } from '../components/data-table.tsx';
import { OnboardingCell } from '../components/OnboardingCell.tsx';
import { RateCell } from '../components/RateCell.tsx';
import { Card, CardBody } from '../components/ui/card.tsx';
import { Label, Select } from '../components/ui/form.tsx';
import { ErrorBox, Loading } from '../components/ui/states.tsx';
import { useApi } from '../lib/api.ts';
import { asOfText, customerRate, relative } from '../lib/format.ts';
import { PageTitle } from '../components/PageTitle.tsx';
import { BrandKindTabs, useBrandKind } from '../components/BrandKindTabs.tsx';
import { EngineTabs, engineLabel, engineSearch, useEngine } from '../components/EngineTabs.tsx';
import { useMe } from '../lib/me.ts';
import { PATHS } from '../lib/paths.ts';

/**
 * 최근 5일 일별 언급률 한 칸 — 하루 표본은 질문당 samples_per_day 라 대개 10 미만이다 —
 * 하한으로 가리지 않고 비율을 보인다(표본 수는 싣지 않는다).
 * 실측(`raw`, admin 전용)이 있으면 실측이 메인이고, 표시값(온보딩)은 실측과 다른 날에만 아래 작은 줄에 — 브랜드 언급률 칸(RateCell)과 같은 판정식이다.
 * 그 행의 어느 날이라도 실측 줄이 있으면 다섯 칸 모두 두 줄로 그려 높이를 맞춘다
 */
function RecentDay({
  days,
  i,
}: {
  days: QueryMetricsRow['recentMention'];
  /** 최근부터 센 순번 — 0 = 어제 */
  i: number;
}) {
  const d = days.at(-1 - i);
  if (!d) return null;
  const differs = (x: (typeof days)[number]) => x.raw !== null && x.raw.k !== x.mention.k;
  const trend = dayTrend(d, days.at(-2 - i));
  return (
    <div className="text-xs tabular-nums leading-tight">
      <div className="flex items-center gap-0.5 text-slate-700">
        {customerRate(d.raw ?? d.mention)}
        {trend === 'up' ? (
          <span className="text-[10px] text-emerald-600" title="전일보다 올랐습니다">
            ▲<span className="sr-only">전일 대비 상승</span>
          </span>
        ) : trend === 'down' ? (
          <span className="text-[10px] text-rose-600" title="전일보다 내렸습니다">
            ▼<span className="sr-only">전일 대비 하락</span>
          </span>
        ) : null}
      </div>
      {days.slice(-RECENT_DAYS).some(differs) ? (
        <div className="text-[11px] text-slate-500">{differs(d) ? customerRate(d.mention) : '\u00a0'}</div>
      ) : null}
    </div>
  );
}

/**
 * 전일 대비 증감 — 화면의 큰 숫자(실측이 있으면 실측)끼리, **보이는 퍼센트(반올림)** 로 비교한다.
 * 어느 쪽이든 그날 표본이 없으면 말하지 않는다. 같으면 아무것도 그리지 않는다
 */
function dayTrend(
  d: QueryMetricsRow['recentMention'][number],
  prev: QueryMetricsRow['recentMention'][number] | undefined,
): 'up' | 'down' | null {
  if (!prev) return null;
  const a = d.raw ?? d.mention;
  const b = prev.raw ?? prev.mention;
  if (!a.n || !b.n) return null;
  const diff = Math.round((a.k / a.n) * 100) - Math.round((b.k / b.n) * 100);
  return diff > 0 ? 'up' : diff < 0 ? 'down' : null;
}

/**
 * 칸 제목 — 날짜는 서버가 정한다(어제까지 5일). `i` 는 **최근부터** 센다 — 서버는 오래된 날부터 주고
 * 화면은 어제를 왼쪽에 둔다(운영자 결정 2026-11-03). 모든 행이 같은 5일이라 첫 행에서 읽는다
 */
const RECENT_DAYS = 5;
const mmdd = (date: string | undefined) => (date ? `${date.slice(5, 7)}/${date.slice(8, 10)}` : '');

/**
 * ★ `customer` 는 **고객·대행사에게 보이는 표**라는 뜻이다. 표본에 관한 정보를 전부 뺀다 —
 *   비율은 `customerRate`(k/n·CI·표본 부족 없음)로, '오늘 N/M'·'마지막 샘플' 칸은 아예 그리지 않는다
 *   (260921-client-report-chart-only). 이 표는 `/app/brands/$id` 에도 마운트된다.
 */
export function queryColumns(
  showOurSite: boolean,
  showBrand: boolean,
  customer = false,
): ColumnDef<QueryMetricsRow, unknown>[] {
  const cols: ColumnDef<QueryMetricsRow, unknown>[] = [
    {
      header: '질문',
      accessorKey: 'query',
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.query}</div>
          <div className="text-xs text-slate-500">{row.original.brandNames.join(' · ')}</div>
        </div>
      ),
    },
  ];
  if (showBrand) cols.push({ header: '고객', accessorKey: 'brandName' });
  // ★ 운영자 표도 비율만 보인다 — k/n·95% CI 는 질문 상세에서 본다(운영자 결정 2026-11-03).
  //   본문·장소 카드·검색 유발 칸도 그때 걷었다(질문 상세에는 남아 있다)
  const plain = !customer;
  cols.push({
    header: '브랜드 언급률',
    id: 'mention',
    // 정렬도 화면의 큰 숫자(실측이 있으면 실측)로 한다
    accessorFn: (r) => {
      const m = r.raw?.mention ?? r.mention;
      return m.n ? m.k / m.n : -1;
    },
    cell: ({ row }) => (
      <RateCell
        rate={row.original.mention}
        emphasize
        raw={row.original.raw?.mention}
        customer={customer}
        plain={plain}
      />
    ),
  });
  for (let i = 0; i < RECENT_DAYS; i++)
    cols.push({
      id: `recent${i}`,
      header: ({ table }) => mmdd(table.getCoreRowModel().rows[0]?.original.recentMention.at(-1 - i)?.date),
      enableSorting: false,
      cell: ({ row }) => <RecentDay days={row.original.recentMention} i={i} />,
    });
  if (showOurSite)
    cols.push({
      header: '우리 사이트 인용',
      id: 'ours',
      accessorFn: (r) => r.ourSite?.k ?? 0,
      // 인용 > 언급 경고 아이콘은 걷었다(운영자 결정 2026-11-03) — 인용 일치율 칸이 같은 것을 비율로 보인다
      cell: ({ row }) => <RateCell rate={row.original.ourSite} plain={plain} />,
    });
  // 인용 일치율 — 우리 사이트가 인용된 샘플 중 브랜드도 언급된 비율. 분모가 `우리 사이트 인용` 의 k 다
  if (showOurSite)
    cols.push({
      header: '인용 일치율',
      id: 'oursMention',
      accessorFn: (r) => (r.ourSiteMention?.n ? r.ourSiteMention.k / r.ourSiteMention.n : -1),
      cell: ({ row }) => (
        <div title="우리 사이트가 인용된 답변 중 브랜드도 언급된 비율입니다 (실측)">
          {/* 분모가 「우리 사이트 인용 수」라 대부분 10 미만이다 — 하한 없이 비율을 보인다(운영자 결정 2026-10-12) */}
          <RateCell rate={row.original.ourSiteMention} minSamples={1} plain={plain} />
        </div>
      ),
    });
  // 온보딩 상태 — 위 '실측' 줄이 왜 다른지를 설명하는 칸이다. 운영자 화면에만 있다
  if (showOurSite)
    cols.push({
      header: '온보딩',
      id: 'onboarding',
      accessorFn: (r) => r.onboarding?.factor ?? 1,
      cell: ({ row }) => <OnboardingCell state={row.original.onboarding} />,
    });
  // 수집 현황 — 표본 수를 그대로 드러낸다('오늘 3/10'). **고객 표에는 싣지 않는다**
  if (!customer)
    cols.push(
      {
        header: '오늘',
        id: 'today',
        accessorFn: (r) => r.todayDone,
        cell: ({ row }) => (
          <span className="tabular-nums">
            {row.original.todayDone}/{row.original.todayPlanned}
          </span>
        ),
      },
      {
        header: '마지막 샘플',
        accessorKey: 'lastSampleAt',
        cell: ({ row }) => <span className="text-slate-500">{relative(row.original.lastSampleAt)}</span>,
      },
    );
  return cols;
}

/**
 * ★ 이제 **운영자 영역(/admin) 전용**이다 — 고객·대행사는 report.tsx 의 그래프 화면을 본다
 *   (260921-client-report-chart-only). 그래서 아래 `role !== 'member'` 셋은 **늘 참인 죽은 분기**다.
 *   지우지 않고 둔다: 이 화면이 다시 공용이 될 때 되살릴 자리를 남겨 두는 편이 싸고, 위 `queryColumns`
 *   는 brands.tsx(두 영역 공용)가 계속 쓰므로 역할 플래그 자체는 살아 있다 — 단 거기서는 자기 값을
 *   따로 넘긴다(`queryColumns(isAdmin, false, !isAdmin)`).
 */
export function OverviewPage() {
  const me = useMe();
  const [brandId, setBrandId] = useState('');
  const [days, setDays] = useState(7);
  const role = me.data?.role;
  // 「실제 | 테스트」 탭 — 합산도 고객 선택지도 탭을 따른다(261023-test-brand)
  const kind = useBrandKind();
  // 「ChatGPT | Gemini」 탭 — 운영자만(261104-gemini-concurrent-lane). 다른 역할은 보내지 않는다(서버가 403)
  const tabEngine = useEngine();
  const engine = role === 'admin' ? tabEngine : undefined;
  const brands = useApi(registry.listBrands, { query: { brandKind: kind } }, { enabled: role !== 'member' });
  // 탭을 바꾸면 다른 쪽 고객은 선택지에 없다 — 그때는 「전체」로 본다
  const picked = brands.data?.brands.some((c) => c.id === brandId) ? brandId : '';
  const overview = useApi(metrics.overview, { query: { days, brandId: picked || undefined, brandKind: kind, engine } });

  return (
    <>
      <PageTitle
        title={engine ? `질문별 브랜드 노출 — ${engineLabel(engine)}` : '질문별 브랜드 노출'}
        description={`${asOfText(overview.data?.asOf)} ${days}일 · 같은 질문을 반복해 잰 기록입니다 · 표본이 10개 미만인 비율은 흐리게 표시합니다`}
      />
      {role === 'admin' ? (
        <div className="flex flex-wrap items-center gap-4">
          <EngineTabs />
          <span className="h-5 w-px bg-slate-200" aria-hidden />
          <BrandKindTabs />
        </div>
      ) : null}
      <Card>
        <CardBody className="flex flex-wrap gap-3">
          {role !== 'member' ? (
            <Label className="w-64">
              고객
              <Select value={picked} onChange={(e) => setBrandId(e.target.value)}>
                <option value="">전체 ({brands.data?.brands.length ?? 0}곳)</option>
                {brands.data?.brands.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </Label>
          ) : null}
          <Label className="w-32">
            기간
            <Select value={days} onChange={(e) => setDays(Number(e.target.value))}>
              {[7, 14, 30, 90].map((d) => (
                <option key={d} value={d}>
                  {d}일
                </option>
              ))}
            </Select>
          </Label>
        </CardBody>
      </Card>
      <Card>
        {overview.isLoading ? (
          <Loading />
        ) : overview.error ? (
          <ErrorBox error={overview.error} />
        ) : (
          <DataTable
            columns={queryColumns(role === 'admin', role !== 'member')}
            data={overview.data?.rows ?? []}
            empty={kind === 'test' ? '테스트 브랜드의 질문이 없습니다' : '아직 측정 중인 질문이 없습니다'}
            rowLink={(r) => ({
              to: PATHS.admin.query,
              params: { id: r.queryId },
              search: engine ? engineSearch(engine) : {},
            })}
          />
        )}
      </Card>
    </>
  );
}
